import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Users, 
  Calendar, 
  MessageSquare, 
  TrendingUp, 
  RefreshCw, 
  Mail, 
  Phone, 
  Building, 
  Clock,
  Eye,
  FileText
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { AdminPanel } from '@/components/AdminPanel';

interface Lead {
  id: string;
  name: string;
  email: string;
  source: string;
  offer_type: string | null;
  created_at: string;
}

interface QualifiedLead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string;
  industry: string;
  business_size: string;
  current_challenges: string;
  interested_in: string[];
  budget: string;
  timeline: string;
  additional_notes: string | null;
  source: string;
  created_at: string;
}

interface Booking {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  booking_date: string;
  booking_time: string;
  service_type: string | null;
  status: string | null;
  created_at: string;
}

interface ChatMessage {
  id: string;
  session_id: string;
  user_message: string;
  bot_response: string;
  created_at: string;
}

interface AnalyticsSummary {
  total_visits: number;
  unique_visitors: number;
  returning_visitors: number;
  identified_visitors: number;
  conversion_rate: number;
}

const AdminDashboard = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [qualifiedLeads, setQualifiedLeads] = useState<QualifiedLead[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch leads
      const { data: leadsData } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      setLeads(leadsData || []);

      // Fetch qualified leads
      const { data: qualifiedData } = await supabase
        .from('qualified_leads')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      setQualifiedLeads(qualifiedData || []);

      // Fetch bookings
      const { data: bookingsData } = await supabase
        .from('bookings')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      setBookings(bookingsData || []);

      // Fetch chat history
      const { data: chatData } = await supabase
        .from('chat_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      setChatHistory(chatData || []);

      // Fetch analytics summary
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const { data: analyticsData } = await supabase
        .rpc('get_visitor_analytics', {
          start_date: startDate.toISOString(),
          end_date: new Date().toISOString(),
        });
      if (analyticsData && analyticsData.length > 0) {
        setAnalytics(analyticsData[0] as AnalyticsSummary);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string | null) => {
    const statusMap: Record<string, string> = {
      pending: 'bg-yellow-500/10 text-yellow-600',
      confirmed: 'bg-green-500/10 text-green-600',
      cancelled: 'bg-red-500/10 text-red-600',
      completed: 'bg-blue-500/10 text-blue-600'
    };
    return statusMap[status || 'pending'] || statusMap.pending;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-background/95">
      <Navigation />
      <AdminPanel />
      
      <main className="container mx-auto px-4 py-24">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground">
                Manage leads, bookings, and view analytics
              </p>
            </div>
            <Button onClick={fetchAllData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{leads.length}</div>
                <p className="text-xs text-muted-foreground">All time</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Qualified Leads</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{qualifiedLeads.length}</div>
                <p className="text-xs text-muted-foreground">High intent</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Bookings</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{bookings.length}</div>
                <p className="text-xs text-muted-foreground">Demo requests</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Chat Sessions</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{chatHistory.length}</div>
                <p className="text-xs text-muted-foreground">AI conversations</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Visitors (30d)</CardTitle>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics?.unique_visitors || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {analytics?.conversion_rate || 0}% conversion
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Data Tabs */}
          <Tabs defaultValue="leads" className="space-y-4">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="leads">Leads</TabsTrigger>
              <TabsTrigger value="qualified">Qualified</TabsTrigger>
              <TabsTrigger value="bookings">Bookings</TabsTrigger>
              <TabsTrigger value="chat">Chat History</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
            </TabsList>

            {/* Leads Tab */}
            <TabsContent value="leads">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Recent Leads ({leads.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Offer</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leads.map((lead) => (
                          <TableRow key={lead.id}>
                            <TableCell className="font-medium">{lead.name}</TableCell>
                            <TableCell>
                              <a href={`mailto:${lead.email}`} className="text-primary hover:underline flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {lead.email}
                              </a>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{lead.source}</Badge>
                            </TableCell>
                            <TableCell>{lead.offer_type || '-'}</TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {formatDate(lead.created_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {leads.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                              No leads yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Qualified Leads Tab */}
            <TabsContent value="qualified">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Qualified Leads ({qualifiedLeads.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Contact</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Industry</TableHead>
                          <TableHead>Budget</TableHead>
                          <TableHead>Timeline</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {qualifiedLeads.map((lead) => (
                          <TableRow key={lead.id}>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="font-medium">{lead.name}</div>
                                <div className="text-sm text-muted-foreground flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {lead.email}
                                </div>
                                {lead.phone && (
                                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {lead.phone}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Building className="h-3 w-3" />
                                {lead.company}
                              </div>
                              <div className="text-xs text-muted-foreground">{lead.business_size}</div>
                            </TableCell>
                            <TableCell>{lead.industry}</TableCell>
                            <TableCell>
                              <Badge variant="secondary">{lead.budget}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm">
                                <Clock className="h-3 w-3" />
                                {lead.timeline}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {formatDate(lead.created_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {qualifiedLeads.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                              No qualified leads yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Bookings Tab */}
            <TabsContent value="bookings">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Demo Bookings ({bookings.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Contact</TableHead>
                          <TableHead>Date & Time</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Submitted</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookings.map((booking) => (
                          <TableRow key={booking.id}>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="font-medium">{booking.name}</div>
                                <div className="text-sm text-muted-foreground">{booking.email}</div>
                                {booking.phone && (
                                  <div className="text-sm text-muted-foreground">{booking.phone}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">
                                {new Date(booking.booking_date).toLocaleDateString('en-US', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </div>
                              <div className="text-sm text-muted-foreground">{booking.booking_time}</div>
                            </TableCell>
                            <TableCell>{booking.service_type || 'AI Demo'}</TableCell>
                            <TableCell>
                              <Badge className={getStatusBadge(booking.status)}>
                                {booking.status || 'pending'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {formatDate(booking.created_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {bookings.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                              No bookings yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Chat History Tab */}
            <TabsContent value="chat">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    AI Chat History ({chatHistory.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {chatHistory.map((chat) => (
                      <div key={chat.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>Session: {chat.session_id.slice(0, 20)}...</span>
                          <span>{formatDate(chat.created_at)}</span>
                        </div>
                        <div className="space-y-2">
                          <div className="bg-muted/50 rounded-lg p-3">
                            <p className="text-xs font-medium text-muted-foreground mb-1">User:</p>
                            <p className="text-sm">{chat.user_message}</p>
                          </div>
                          <div className="bg-primary/5 rounded-lg p-3">
                            <p className="text-xs font-medium text-primary mb-1">Neil AI:</p>
                            <p className="text-sm">{chat.bot_response}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {chatHistory.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No chat history yet
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Analytics Overview (Last 30 Days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <div className="text-3xl font-bold text-primary">{analytics.total_visits}</div>
                        <div className="text-sm text-muted-foreground">Total Visits</div>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <div className="text-3xl font-bold text-primary">{analytics.unique_visitors}</div>
                        <div className="text-sm text-muted-foreground">Unique Visitors</div>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <div className="text-3xl font-bold text-primary">{analytics.returning_visitors}</div>
                        <div className="text-sm text-muted-foreground">Returning Visitors</div>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <div className="text-3xl font-bold text-primary">{analytics.conversion_rate}%</div>
                        <div className="text-sm text-muted-foreground">Conversion Rate</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading analytics...
                    </div>
                  )}
                  <div className="mt-6">
                    <Button variant="outline" onClick={() => window.location.href = '/analytics'}>
                      View Detailed Analytics →
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
